import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sections } from '@/constants/navigation';

const pathById = new Map(sections.map((section) => [section.id, section.path]));
const idByPath = new Map(sections.map((section) => [section.path, section.id]));
const DEFAULT_TRIGGER_RATIO = 0.28;
const DEFAULT_OBSERVER_THRESHOLDS: number[] = [0, 0.01];
const AUTO_SCROLL_RELEASE_MS = 120;
const AUTO_SCROLL_MIN_LOCK_MS = 120;
const SMOOTH_SCROLL_RELEASE_MS = 1600;
const SCROLL_RELEASE_BUFFER_MS = 100;
const MENU_CLICK_LOCK_EVENT = 'oi:scroll-sync-menu-click-lock';

function getEnvVar(name: string): string | undefined {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return env?.[name];
}

function getTriggerRatio(): number {
  const raw = getEnvVar('VITE_SCROLL_URL_SYNC_TRIGGER_RATIO');
  const parsed = Number.parseFloat(raw ?? '');
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed >= 1) return DEFAULT_TRIGGER_RATIO;
  return parsed;
}

function shouldUseSmoothScrollLock(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function computeAutoScrollLockMs(): number {
  const releaseMs = shouldUseSmoothScrollLock() ? SMOOTH_SCROLL_RELEASE_MS : AUTO_SCROLL_RELEASE_MS;
  return Math.max(AUTO_SCROLL_MIN_LOCK_MS, releaseMs) + SCROLL_RELEASE_BUFFER_MS;
}

export default function ScrollUrlSync() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pendingPathRef = useRef<string | null>(null);
  const pauseScrollSyncUntilRef = useRef<number>(0);
  const autoScrollLockMsRef = useRef<number>(computeAutoScrollLockMs());
  const triggerRatioRef = useRef<number>(getTriggerRatio());

  useEffect(() => {
    pendingPathRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const onMenuClickLock = (event: Event) => {
      const customEvent = event as CustomEvent<{ path?: string }>;
      const lockedPath = customEvent.detail?.path;
      if (!lockedPath) return;
      pendingPathRef.current = lockedPath;
      autoScrollLockMsRef.current = computeAutoScrollLockMs();
      pauseScrollSyncUntilRef.current = Date.now() + autoScrollLockMsRef.current;
    };

    window.addEventListener(MENU_CLICK_LOCK_EVENT, onMenuClickLock as EventListener);
    return () =>
      window.removeEventListener(MENU_CLICK_LOCK_EVENT, onMenuClickLock as EventListener);
  }, []);

  useEffect(() => {
    const sectionElements = sections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => element !== null);

    if (!sectionElements.length) return;

    const getActiveSection = (): HTMLElement | null => {
      const triggerY = Math.round(window.innerHeight * triggerRatioRef.current);
      let candidate: HTMLElement | null = null;

      for (const element of sectionElements) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= triggerY) {
          candidate = element;
        } else {
          break;
        }
      }

      return candidate ?? sectionElements[0] ?? null;
    };

    const syncFromScrollPosition = () => {
      if (Date.now() <= pauseScrollSyncUntilRef.current) return;

      const activeSection = getActiveSection();
      if (!activeSection) return;

      const pendingPath = pendingPathRef.current;
      if (pendingPath) {
        const pendingId = idByPath.get(pendingPath);
        if (pendingId && activeSection.id !== pendingId) return;
        pendingPathRef.current = null;
        return;
      }

      const nextPath = pathById.get(activeSection.id);
      if (!nextPath || nextPath === pathname) return;

      navigate(nextPath, { replace: true });
    };

    const observer = new IntersectionObserver(
      () => {
        syncFromScrollPosition();
      },
      {
        threshold: DEFAULT_OBSERVER_THRESHOLDS,
      },
    );

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        syncFromScrollPosition();
      });
    };

    sectionElements.forEach((element) => observer.observe(element));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    syncFromScrollPosition();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [navigate, pathname]);

  return null;
}
